import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { PlanViewer } from "../components/PlanViewer/PlanViewer";
import type {
  ViewerMode,
  MarkerLabelField,
} from "../components/PlanViewer/PlanViewer";
import { QRScanner } from "../components/QRScanner/QRScanner";
import { MarkerForm } from "../components/MarkerForm/MarkerForm";
import { Spinner } from "../components/UI/Spinner";
import { usePlanStore } from "../store/planStore";
import { useMarkerStore } from "../store/markerStore";
import { useAuthStore } from "../store/authStore";
import { listPlansRequest } from "../api/plansApi";
import {
  listMarkersRequest,
  createMarkerRequest,
  updateMarkerRequest,
  deleteMarkerRequest,
} from "../api/markersApi";
import {
  registerVisitRequest,
  visitByQrRequest,
} from "../api/visitsApi";
import { useOfflineQueue } from "../hooks/useOfflineQueue";
import type { Marker, MarkerCategory } from "../types/domain.types";
import { exportPlanImage, downloadBlob } from "../utils/exportPlan";

function extractErr(e: unknown, fallback: string): string {
  if (isAxiosError(e)) {
    return (
      (e.response?.data as { error?: { message?: string } } | undefined)?.error
        ?.message ?? fallback
    );
  }
  return fallback;
}

export function ViewerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { plans, activePlan, setPlans, setActivePlan } = usePlanStore();
  const { markers, setMarkers, addMarker, updateMarker, updateMarkers, removeMarker } =
    useMarkerStore();
  const {
    pending,
    enqueue,
    drain,
    syncing,
    online,
  } = useOfflineQueue(id);

  const [pendingCoord, setPendingCoord] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [qrCode, setQrCode] = useState<string>("");
  const [category, setCategory] = useState<MarkerCategory | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [markerError, setMarkerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [mode, setMode] = useState<ViewerMode>("register");
  const [labelField, setLabelField] = useState<MarkerLabelField>(() => {
    const saved =
      typeof window !== "undefined"
        ? window.localStorage.getItem("im.labelField")
        : null;
    return saved === "comment" ? "comment" : "qrCode";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("im.labelField", labelField);
    }
  }, [labelField]);
  const [visitFeedback, setVisitFeedback] = useState<string | null>(null);
  const [deleteFeedback, setDeleteFeedback] = useState<string | null>(null);
  const [editFeedback, setEditFeedback] = useState<string | null>(null);
  const [editingMarker, setEditingMarker] = useState<Marker | null>(null);
  const [comment, setComment] = useState("");

  const canCreateMarker =
    user?.role === "ADMIN" || user?.role === "TECHNICIAN";
  const canVisit = canCreateMarker; // mismos roles por ahora
  const canExport = user?.role === "ADMIN";

  // Cargar plan activo (desde store o fetch)
  useEffect(() => {
    if (!id) return;
    const existing = plans.find((p) => p.id === id) ?? null;
    if (existing) {
      setActivePlan(existing);
      return;
    }
    let active = true;
    setLoadingPlan(true);
    setPlanError(null);
    listPlansRequest()
      .then((list) => {
        if (!active) return;
        setPlans(list);
        const found = list.find((p) => p.id === id) ?? null;
        if (!found) {
          setPlanError("Plano no encontrado");
        } else {
          setActivePlan(found);
        }
      })
      .catch((e) => {
        if (active) setPlanError(extractErr(e, "Error al cargar plano"));
      })
      .finally(() => {
        if (active) setLoadingPlan(false);
      });
    return () => {
      active = false;
    };
  }, [id, plans, setPlans, setActivePlan]);

  // Cargar marcadores del plano
  const loadMarkers = useCallback(() => {
    if (!id) return;
    let active = true;
    setMarkerError(null);
    listMarkersRequest({ planId: id })
      .then((list) => {
        if (active) setMarkers(list);
      })
      .catch((e) => {
        if (active) setMarkerError(extractErr(e, "Error al cargar marcadores"));
      });
    return () => {
      active = false;
    };
  }, [id, setMarkers]);

  useEffect(() => {
    return loadMarkers();
  }, [loadMarkers]);

  async function handleSaveMarker({
    qrCode: qr,
    category: cat,
    comment: com,
  }: {
    qrCode: string;
    category: MarkerCategory;
    comment: string;
  }) {
    if (!id || !pendingCoord || !qr || !user) return;
    setSubmitting(true);
    setSubmitError(null);

    if (editingMarker) {
      try {
        const res = await updateMarkerRequest(editingMarker.id, {
          qrCode: qr,
          category: cat,
          coordX: pendingCoord.x,
          coordY: pendingCoord.y,
          comment: com || undefined,
        });
        updateMarker(editingMarker.id, {
          qrCode: res.qrCode,
          category: res.category,
          coordX: res.coordX,
          coordY: res.coordY,
          comment: res.comment,
        });
        setEditFeedback("Marcador actualizado");
        setEditingMarker(null);
        setPendingCoord(null);
        setQrCode("");
        setCategory(null);
        setComment("");
      } catch (e) {
        setSubmitError(extractErr(e, "No se pudo actualizar el marcador"));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const payload = {
      planId: id,
      qrCode: qr,
      category: cat,
      coordX: pendingCoord.x,
      coordY: pendingCoord.y,
      comment: com || undefined,
    };

    // Si no hay conexion, encolamos directamente.
    if (!online) {
      try {
        await enqueue({
          ...payload,
          userId: user.id,
          userEmail: user.email,
        });
        setPendingCoord(null);
        setQrCode("");
        setComment("");
      } catch (e) {
        setSubmitError(extractErr(e, "No se pudo encolar el marcador"));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    try {
      const res = await createMarkerRequest(payload);
      const newMarker: Marker = {
        id: res.id,
        qrCode: qr,
        category: cat,
        coordX: pendingCoord.x,
        coordY: pendingCoord.y,
        comment: com || null,
        createdAt: res.createdAt,
        lastVisitedAt: null,
        lastVisitedBy: null,
        user: { id: user.id, email: user.email },
      };
      addMarker(newMarker);
      setPendingCoord(null);
      setQrCode("");
      setCategory(null);
      setComment("");
    } catch (e) {
      // Si es error de red (sin response), encolamos como offline.
      const isNetworkErr =
        isAxiosError(e) && !e.response;
      if (isNetworkErr) {
        try {
          await enqueue({
            ...payload,
            userId: user.id,
            userEmail: user.email,
          });
          setPendingCoord(null);
          setQrCode("");
          setComment("");
        } catch (err) {
          setSubmitError(extractErr(err, "No se pudo encolar el marcador"));
        }
      } else {
        setSubmitError(extractErr(e, "No se pudo guardar el marcador"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleEditMarker(marker: Marker) {
    setEditingMarker(marker);
    setPendingCoord({ x: marker.coordX, y: marker.coordY });
    setQrCode(marker.qrCode);
    setCategory(marker.category);
    setComment(marker.comment ?? "");
    setSubmitError(null);
  }

  // --- Modo explorador: handlers de visita ---
  async function handleVisitMarker(markerId: string) {
    if (!user) return;
    setVisitFeedback(null);
    try {
      const res = await registerVisitRequest({ markerId });
      updateMarker(markerId, {
        lastVisitedAt: res.visitedAt,
        lastVisitedBy: { id: user.id, email: user.email },
      });
      setVisitFeedback("Visita registrada");
    } catch (e) {
      setVisitFeedback(extractErr(e, "No se pudo registrar la visita"));
    }
  }

  async function handleDeleteMarker(markerId: string) {
    setDeleteFeedback(null);
    try {
      await deleteMarkerRequest(markerId);
      removeMarker(markerId);
      setDeleteFeedback("Marcador eliminado");
    } catch (e) {
      setDeleteFeedback(extractErr(e, "No se pudo eliminar el marcador"));
    }
  }

  async function handleExportPlan() {
    if (!activePlan) return;
    setExporting(true);
    setExportError(null);
    try {
      const blob = await exportPlanImage(
        activePlan,
        markers,
        undefined,
        labelField,
      );
      const safeName = activePlan.name.replace(/[^a-z0-9-_]+/gi, "_");
      downloadBlob(blob, `${safeName}.pdf`);
    } catch (e) {
      setExportError(extractErr(e, "No se pudo exportar el plano"));
    } finally {
      setExporting(false);
    }
  }

  async function handleVisitByQr(qr: string) {
    if (!id || !user) return;
    setVisitFeedback(null);
    try {
      const res = await visitByQrRequest({ planId: id, qrCode: qr });
      updateMarkers(res.markerIds, {
        lastVisitedAt: res.visitedAt,
        lastVisitedBy: { id: user.id, email: user.email },
      });
      setVisitFeedback(
        `Visita registrada en ${res.visitedCount} marcador(es)`,
      );
    } catch (e) {
      setVisitFeedback(extractErr(e, "QR no encontrado en este plano"));
    }
  }

  if (loadingPlan) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size={40} />
      </div>
    );
  }

  if (planError && !activePlan) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-slate-300">
        <p className="text-sm text-red-400">{planError}</p>
        <button
          onClick={() => navigate("/plans")}
          className="rounded-md bg-brand-light px-3 py-2 text-xs font-semibold text-white"
        >
          Volver a planos
        </button>
      </div>
    );
  }

  if (!activePlan) return null;

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b border-slate-800 bg-slate-900/60 px-4 py-2">
        <div>
          <Link to="/plans" className="text-xs text-slate-400 hover:underline">
            ← Planos
          </Link>
          <h1 className="text-sm font-semibold text-slate-100">
            {activePlan.name}
            <span className="ml-2 text-xs font-normal text-slate-400">
              {activePlan.clientName}
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex items-center rounded-md border border-slate-700 bg-slate-900 p-0.5 text-xs"
            role="group"
            aria-label="Etiqueta de marcadores"
            title="Elige qué dato mostrar sobre cada marcador"
          >
            <button
              type="button"
              onClick={() => setLabelField("qrCode")}
              className={`rounded px-2 py-1 font-semibold transition ${
                labelField === "qrCode"
                  ? "bg-brand-light text-white"
                  : "text-slate-300 hover:bg-slate-800"
              }`}
            >
              ID
            </button>
            <button
              type="button"
              onClick={() => setLabelField("comment")}
              className={`rounded px-2 py-1 font-semibold transition ${
                labelField === "comment"
                  ? "bg-brand-light text-white"
                  : "text-slate-300 hover:bg-slate-800"
              }`}
            >
              Comentario
            </button>
          </div>
          {canExport && (
            <button
              type="button"
              onClick={() => void handleExportPlan()}
              disabled={exporting}
              title="Descarga el plano completo a máxima resolución con los marcadores"
              className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            >
              {exporting ? "Exportando..." : "Descargar plano"}
            </button>
          )}
          <Link
            to="/history"
            className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800"
          >
            Historial
          </Link>
        </div>
      </header>
      {exportError && (
        <p
          role="alert"
          className="border-b border-red-500/40 bg-red-500/10 px-4 py-1 text-xs text-red-300"
        >
          {exportError}
        </p>
      )}

      <div className="grid flex-1 grid-cols-1 lg:grid-cols-[1fr_360px]">
        <div className="relative h-[60vh] lg:h-auto">
          <PlanViewer
            plan={activePlan}
            markers={markers}
            pendingMarkers={pending}
            pendingCoord={pendingCoord}
            onPickCoord={setPendingCoord}
            mode={mode}
            onMarkerVisit={(mid) => void handleVisitMarker(mid)}
            onMarkerDelete={(mid) => void handleDeleteMarker(mid)}
            onMarkerEdit={(m) => handleEditMarker(m)}
            labelField={labelField}
          />
        </div>

        <aside className="flex flex-col gap-3 overflow-y-auto border-t border-slate-800 bg-slate-950 p-4 lg:border-l lg:border-t-0">
          {canVisit && (
            <div className="flex rounded-md border border-slate-700 bg-slate-900 p-0.5 text-xs">
              <button
                type="button"
                onClick={() => {
                  setMode("register");
                  setVisitFeedback(null);
                  setEditingMarker(null);
                  setPendingCoord(null);
                  setQrCode("");
                  setCategory(null);
                  setComment("");
                  setSubmitError(null);
                }}
                className={`flex-1 rounded px-3 py-1.5 font-semibold transition ${
                  mode === "register"
                    ? "bg-brand-light text-white"
                    : "text-slate-300 hover:bg-slate-800"
                }`}
              >
                Registro
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("explore");
                  setPendingCoord(null);
                  setQrCode("");
                  setCategory(null);
                  setComment("");
                  setEditingMarker(null);
                  setSubmitError(null);
                }}
                className={`flex-1 rounded px-3 py-1.5 font-semibold transition ${
                  mode === "explore"
                    ? "bg-emerald-600 text-white"
                    : "text-slate-300 hover:bg-slate-800"
                }`}
              >
                Explorador
              </button>
            </div>
          )}

          {canCreateMarker && mode === "register" ? (
            <>
              <div className="flex gap-2">
                <button
                  onClick={() => setScannerOpen(true)}
                  className="flex-1 rounded-md bg-brand-light px-3 py-2 text-sm font-semibold text-white hover:bg-brand"
                >
                  {qrCode ? "Re-escanear QR" : "Escanear QR"}
                </button>
                {pendingCoord && (
                  <button
                    onClick={() => setPendingCoord(null)}
                    className="rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
                  >
                    Limpiar coord
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400">
                {pendingCoord
                  ? "Coordenada seleccionada. Completa los datos."
                  : "Toca sobre el plano para elegir la coordenada."}
              </p>
              {markerError && (
                <p role="alert" className="text-xs text-red-400">
                  {markerError}
                </p>
              )}
              <MarkerForm
                qrCode={qrCode}
                onQrCodeChange={setQrCode}
                category={category}
                onCategoryChange={setCategory}
                coord={pendingCoord}
                comment={comment}
                onCommentChange={setComment}
                submitting={submitting}
                error={submitError}
                submitLabel={editingMarker ? "Guardar cambios" : "Guardar marcador"}
                onCancel={() => {
                  setPendingCoord(null);
                  setQrCode("");
                  setCategory(null);
                  setComment("");
                  setEditingMarker(null);
                  setSubmitError(null);
                }}
                onSubmit={handleSaveMarker}
              />
              {deleteFeedback && (
                <p
                  role="status"
                  className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                >
                  {deleteFeedback}
                </p>
              )}
              {editFeedback && (
                <p
                  role="status"
                  className="rounded-md border border-blue-500/40 bg-blue-500/10 px-3 py-2 text-xs text-blue-300"
                >
                  {editFeedback}
                </p>
              )}
            </>
          ) : canVisit && mode === "explore" ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setScannerOpen(true)}
                className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
              >
                Escanear QR para registrar visita
              </button>
              <p className="text-xs text-slate-400">
                Escanea el QR del punto inspeccionado, o toca un marcador en el
                mapa y pulsa <strong>Confirmar visita</strong>.
              </p>
              {visitFeedback && (
                <p
                  role="status"
                  className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-200"
                >
                  {visitFeedback}
                </p>
              )}
              <div className="mt-1 rounded-md border border-slate-800 bg-slate-900/50 p-2 text-[11px] text-slate-400">
                <div className="mb-1 font-semibold text-slate-300">
                  Leyenda
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  Visitado recientemente (&lt; 7 d)
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" />
                  Visitado este mes (&lt; 30 d)
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-400" />
                  Más de 1 mes
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-brand-light" />
                  Sin visitas
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">
              Tu rol (VIEWER) no permite crear marcadores ni registrar visitas.
            </p>
          )}

          {pending.length > 0 && (
            <div className="flex items-center justify-between rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
              <span>
                {pending.length} marcador(es) pendientes de sincronizar
              </span>
              <button
                onClick={() => void drain()}
                disabled={syncing || !online}
                className="rounded border border-amber-400/50 px-2 py-1 text-xs disabled:opacity-50"
              >
                {syncing ? "Sincronizando..." : "Sincronizar ahora"}
              </button>
            </div>
          )}

          <div>
            <h2 className="mb-2 text-xs uppercase tracking-wide text-slate-500">
              Marcadores ({markers.length})
            </h2>
            <ul className="flex flex-col gap-2">
              {markers.slice(0, 20).map((m) => (
                <li
                  key={m.id}
                  className="rounded-md border border-slate-800 bg-slate-900 p-2 text-xs text-slate-300"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono">{m.qrCode}</span>
                    <span className="text-slate-500">
                      {new Date(m.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {m.comment && (
                    <p className="mt-1 text-slate-400">{m.comment}</p>
                  )}
                </li>
              ))}
              {markers.length === 0 && (
                <li className="text-xs text-slate-500">Sin marcadores aún.</li>
              )}
            </ul>
          </div>
        </aside>
      </div>

      <QRScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={(code) => {
          setScannerOpen(false);
          if (mode === "explore") {
            void handleVisitByQr(code);
          } else {
            setQrCode(code);
          }
        }}
      />
    </div>
  );
}
